import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { API_BASE_URL } from '../../config/api';
import BrandWordmark from '../../components/BrandWordmark';
import SignaturePad from '../../components/SignaturePad';

// ── Brand tokens ──────────────────────────────────────────────────────────────
const B = {
  primary: '#2563eb',
  navy: '#0B2D4D',
  yes: '#34C759',
  no: '#FF3B30',
  na: '#FF9500',
  progressFill: '#34C759',
  bg: '#f8fafc',
  cardBg: '#fff',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  sectionHeaderBg: '#f0f4f8',
  sectionHeaderBorder: '#2563eb',
  additionalInfoBorder: '#FF9500',
  additionalInfoBg: '#fffbf5',
  imageUploadBg: '#eff6ff',
  imageUploadBorder: '#2563eb',
};

// ── Types ─────────────────────────────────────────────────────────────────────

type QuestionType =
  | 'text'
  | 'multiple_choice'
  | 'yes_no'
  | 'yes_no_na'
  | 'number'
  | 'date'
  | 'signature'
  | 'text_header'
  | 'email_check'
  | 'image_type';

interface Question {
  question_id: string;
  question_text: string;
  question_type: QuestionType;
  required: boolean;
  image_required?: boolean | number | string;
  force_image_on_yes?: boolean | number | string;
  force_image_on_no?: boolean | number | string;
  show_conditions?: any;
  options?: string[];
  allow_multiple_selections?: boolean;
  min_selections?: number;
  max_selections?: number;
  allow_video_attachment?: boolean | number | string;
}

interface TokenData {
  templateName: string;
  businessName: string;
  recipientEmail: string | null;
  recipientName: string | null;
  customMessage: string | null;
  expiresAt: string;
  questions: Question[];
}

// ── Conditional logic engine (mirrors complete-check.tsx) ─────────────────────

const evaluateSimpleCondition = (
  condition: any,
  response: string | undefined,
  question: Question
): boolean => {
  const { operator, value } = condition;
  const isMultiSelect =
    question?.question_type === 'multiple_choice' && question?.allow_multiple_selections;
  const selectedOptions =
    isMultiSelect && response ? response.split(', ').map((o) => o.trim()) : [];

  switch (operator) {
    case 'equals':
      return isMultiSelect ? selectedOptions.includes(value) : response === value;
    case 'notEquals':
      return isMultiSelect ? !selectedOptions.includes(value) : response !== value;
    case 'contains':
      return response ? response.toLowerCase().includes(value.toLowerCase()) : false;
    case 'notContains':
      return response ? !response.toLowerCase().includes(value.toLowerCase()) : true;
    case 'answered':
      return response !== undefined && response !== null && response !== '';
    case 'notAnswered':
      return response === undefined || response === null || response === '';
    case 'greaterThan':
      return response ? parseFloat(response) > parseFloat(value) : false;
    case 'lessThan':
      return response ? parseFloat(response) < parseFloat(value) : false;
    default:
      return true;
  }
};

const evaluateComplexCondition = (
  conditionGroup: any,
  responses: Record<string, string>,
  allQuestions: Question[]
): boolean => {
  if (!conditionGroup || !conditionGroup.conditions) return true;
  const { logic, conditions } = conditionGroup;

  if (logic === 'AND') {
    return conditions.every((cond: any) =>
      cond.logic
        ? evaluateComplexCondition(cond, responses, allQuestions)
        : evaluateSimpleCondition(
            cond,
            responses[cond.questionId],
            allQuestions.find((q) => q.question_id === cond.questionId)!
          )
    );
  } else if (logic === 'OR') {
    return conditions.some((cond: any) =>
      cond.logic
        ? evaluateComplexCondition(cond, responses, allQuestions)
        : evaluateSimpleCondition(
            cond,
            responses[cond.questionId],
            allQuestions.find((q) => q.question_id === cond.questionId)!
          )
    );
  }
  return true;
};

const evaluateCondition = (
  showConditions: any,
  responses: Record<string, string>,
  allQuestions: Question[]
): boolean => {
  if (!showConditions) return true;
  try {
    const conds =
      typeof showConditions === 'string' ? JSON.parse(showConditions) : showConditions;
    if (conds.mode === 'simple') {
      if (conds.conditions?.length > 0) {
        const c = conds.conditions[0];
        const question = allQuestions.find((q) => q.question_id === c.questionId);
        if (!question) return false;
        return evaluateSimpleCondition(c, responses[c.questionId], question);
      }
    } else if (conds.mode === 'advanced') {
      return evaluateComplexCondition(conds, responses, allQuestions);
    }
    return true;
  } catch {
    return true;
  }
};

const filterQuestionsByConditions = (
  questions: Question[],
  responses: Record<string, string>
): Question[] =>
  questions.filter(
    (q) => !q.show_conditions || evaluateCondition(q.show_conditions, responses, questions)
  );

// ── Helpers ───────────────────────────────────────────────────────────────────

const isTruthy = (v?: boolean | number | string) =>
  v === true || v === 1 || v === '1' || v === 'true';

// Compress a File to a JPEG Blob (max 1200px, 80% quality) ready for upload
const compressToBlob = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 1200;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        } else {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Compression failed'));
        },
        'image/jpeg',
        0.8
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};

// Generate a local preview data-URL for display only (small, 600px)
const previewDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
        else { width = Math.round((width * MAX) / height); height = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.6));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Preview failed')); };
    img.src = url;
  });
};

// ── Video validation constants ─────────────────────────────────────────────────
const VIDEO_MAX_DURATION_S = 120; // 2 minutes — only limit shown to users

// Validate video file: returns an error message or null if valid
const validateVideoFile = (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > VIDEO_MAX_DURATION_S) {
        resolve(`Video exceeds the 2-minute duration limit (${Math.round(video.duration)}s).`);
      } else {
        resolve(null);
      }
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null); // Allow on metadata error - backend will validate
    };
    video.src = url;
  });
};

// ── Photo Capture (images + optional videos) ──────────────────────────────────

interface CapturedImage {
  preview: string;  // local data-URL or object URL for display
  file: File;
  isVideo?: boolean;
}

const PhotoCapture: React.FC<{
  questionId: string;
  captured: CapturedImage | null;
  onCapture: (img: CapturedImage) => void;
  onRemove: () => void;
  required?: boolean;
  error?: string;
  allowVideo?: boolean;
}> = ({ captured, onCapture, onRemove, required, error, allowVideo }) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setVideoError(null);
    try {
      const preview = await previewDataUrl(file);
      onCapture({ preview, file, isVideo: false });
    } catch {
      // ignore
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setVideoError(null);
    try {
      const validationError = await validateVideoFile(file);
      if (validationError) {
        setVideoError(validationError);
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      onCapture({ preview: previewUrl, file, isVideo: true });
    } catch {
      // ignore
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleImageChange}
      />
      {allowVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={handleVideoChange}
        />
      )}

      {captured ? (
        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
          {captured.isVideo ? (
            <Box
              component="video"
              src={captured.preview}
              controls
              sx={{
                maxWidth: '100%',
                maxHeight: 280,
                borderRadius: '10px',
                border: '1.5px solid',
                borderColor: B.border,
                display: 'block',
                bgcolor: '#000',
              }}
            />
          ) : (
            <Box
              component="img"
              src={captured.preview}
              alt="Captured"
              sx={{
                maxWidth: '100%',
                maxHeight: 280,
                borderRadius: '10px',
                border: '1.5px solid',
                borderColor: B.border,
                display: 'block',
              }}
            />
          )}
          <Button
            size="small"
            variant="contained"
            onClick={onRemove}
            startIcon={<DeleteIcon fontSize="small" />}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: B.no,
              '&:hover': { bgcolor: '#e0352b' },
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
            }}
          >
            Remove
          </Button>
        </Box>
      ) : (
        <Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Box
              onClick={() => imageInputRef.current?.click()}
              sx={{
                flex: 1,
                minWidth: 140,
                border: '2px dashed',
                borderColor: error ? B.no : B.imageUploadBorder,
                borderRadius: '12px',
                bgcolor: error ? '#fff5f5' : B.imageUploadBg,
                py: 2.5,
                px: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: '#dbeafe', borderColor: '#1d4ed8' },
              }}
            >
              {loading ? (
                <CircularProgress size={28} sx={{ color: B.primary }} />
              ) : (
                <>
                  <CameraAltIcon sx={{ fontSize: 32, color: B.primary, mb: 0.5 }} />
                  <Typography variant="body2" fontWeight={600} color={B.primary}>
                    {required ? 'Photo (Required)' : 'Photo'}
                  </Typography>
                  <Typography variant="caption" color={B.textSecondary} sx={{ display: 'block', mt: 0.25 }}>
                    Camera or gallery
                  </Typography>
                </>
              )}
            </Box>
            {allowVideo && (
              <Box
                onClick={() => videoInputRef.current?.click()}
                sx={{
                  flex: 1,
                  minWidth: 140,
                  border: '2px dashed',
                  borderColor: '#34C759',
                  borderRadius: '12px',
                  bgcolor: '#f0fff4',
                  py: 2.5,
                  px: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: '#dcfce7', borderColor: '#16a34a' },
                }}
              >
                <VideocamIcon sx={{ fontSize: 32, color: '#34C759', mb: 0.5 }} />
                <Typography variant="body2" fontWeight={600} color="#16a34a">
                  Video
                </Typography>
                <Typography variant="caption" color={B.textSecondary} sx={{ display: 'block', mt: 0.25 }}>
                  Max 2 minutes
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}
      {videoError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {videoError}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'details' | 'questions' | 'submitting' | 'success';

const PublicCompleteCheck: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenData, setTokenData] = useState<TokenData | null>(null);

  // Step 1 – user details
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [detailsError, setDetailsError] = useState('');

  // Step 2 – responses
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [additionalNotes, setAdditionalNotes] = useState<Record<string, string>>({});
  const [imageFiles, setImageFiles] = useState<Record<string, CapturedImage>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/email-checks/public/${token}`);
        const data: TokenData = res.data;
        setTokenData(data);
        if (data.recipientName) setName(data.recipientName);
        if (data.recipientEmail) setEmail(data.recipientEmail);
        setPageState('details');
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || 'This link is invalid or has expired.';
        setErrorMessage(msg);
        setPageState('error');
      }
    };
    load();
  }, [token]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const setResponse = (questionId: string, value: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const toggleMultiChoice = (questionId: string, option: string, maxSelections?: number) => {
    const current = responses[questionId] || '';
    const selected = current ? current.split(', ').map((s) => s.trim()).filter(Boolean) : [];
    const idx = selected.indexOf(option);
    if (idx >= 0) {
      selected.splice(idx, 1);
    } else {
      if (maxSelections && selected.length >= maxSelections) {
        return; // at max
      }
      selected.push(option);
    }
    setResponse(questionId, selected.join(', '));
  };

  const setImage = (questionId: string, img: CapturedImage) => {
    setImageFiles((prev) => ({ ...prev, [questionId]: img }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const removeImage = (questionId: string) => {
    setImageFiles((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const visibleQuestions = tokenData
    ? filterQuestionsByConditions(tokenData.questions, responses)
    : [];

  const answeredCount = visibleQuestions.filter(
    (q) => q.question_type !== 'text_header' && responses[q.question_id]
  ).length;
  const totalAnswerable = visibleQuestions.filter((q) => q.question_type !== 'text_header').length;
  const progress = totalAnswerable > 0 ? (answeredCount / totalAnswerable) * 100 : 0;

  // ── Step 1 submit ──────────────────────────────────────────────────────────

  const handleDetailsNext = () => {
    if (!name.trim()) {
      setDetailsError('Please enter your name.');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setDetailsError('Please enter a valid email address.');
      return;
    }
    setDetailsError('');
    setPageState('questions');
  };

  // ── Step 2 submit ──────────────────────────────────────────────────────────

  const validateAndSubmit = async () => {
    const errors: Record<string, string> = {};
    visibleQuestions.forEach((q) => {
      if (q.question_type === 'text_header') return;

      const isImageReq = isTruthy(q.image_required);
      const val = responses[q.question_id];

      if (q.question_type === 'image_type') {
        if (q.required && !imageFiles[q.question_id]) {
          errors[q.question_id] = 'A photo is required for this question.';
        }
      } else {
        if (q.required && !val?.trim()) {
          errors[q.question_id] = 'This question requires an answer.';
        }
        if (isImageReq && val !== 'N/A' && !imageFiles[q.question_id]) {
          errors[q.question_id] = errors[q.question_id] || 'A photo is required for this question.';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const firstId = Object.keys(errors)[0];
      document.getElementById(`q-${firstId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setPageState('submitting');
    try {
      // Upload all captured images/videos to Bunny CDN first
      const uploadedUrls: Record<string, string> = {};
      const questionsWithMedia = visibleQuestions.filter(
        (q) => q.question_type !== 'text_header' && imageFiles[q.question_id]
      );

      await Promise.all(
        questionsWithMedia.map(async (q) => {
          const captured = imageFiles[q.question_id];
          try {
            if (captured.isVideo) {
              // Upload video to the dedicated video endpoint
              const formData = new FormData();
              formData.append('video', captured.file, captured.file.name || `video_${q.question_id}.mp4`);
              const res = await axios.post(
                `${API_BASE_URL}/email-checks/public/${token}/upload-video`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
              uploadedUrls[q.question_id] = res.data.url;
            } else {
              // Upload image (compressed) to the image endpoint
              const blob = await compressToBlob(captured.file);
              const formData = new FormData();
              formData.append('image', blob, `photo_${q.question_id}.jpg`);
              const res = await axios.post(
                `${API_BASE_URL}/email-checks/public/${token}/upload-image`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
              );
              uploadedUrls[q.question_id] = res.data.url;
            }
          } catch {
            uploadedUrls[q.question_id] = captured.isVideo
              ? '[Video captured — upload failed]'
              : '[Photo captured — upload failed]';
          }
        })
      );

      const payload = visibleQuestions
        .filter((q) => q.question_type !== 'text_header')
        .map((q) => {
          const baseText = responses[q.question_id] || '';
          const notes = additionalNotes[q.question_id] || '';
          const imageUrl = uploadedUrls[q.question_id] || '';

          let responseText = baseText;
          if (q.question_type === 'image_type') {
            responseText = imageUrl;
          } else {
            if (notes) {
              responseText = baseText ? `${baseText}\n\nAdditional notes: ${notes}` : notes;
            }
            if (imageUrl) {
              responseText = responseText
                ? `${responseText}\n\nPhoto: ${imageUrl}`
                : `Photo: ${imageUrl}`;
            }
          }

          return {
            questionId: q.question_id,
            questionText: q.question_text,
            questionType: q.question_type,
            responseText,
          };
        });

      await axios.post(`${API_BASE_URL}/email-checks/public/${token}/submit`, {
        recipientName: name.trim(),
        recipientEmail: email.trim(),
        responses: payload,
      });
      setPageState('success');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to submit. Please try again.';
      setErrorMessage(msg);
      setPageState('error');
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderYesNoButtons = (q: Question, options: string[]) => {
    const val = responses[q.question_id] || '';
    const err = validationErrors[q.question_id];
    const getBg = (opt: string) => {
      if (val !== opt) return B.cardBg;
      if (opt === 'Yes') return B.yes;
      if (opt === 'No') return B.no;
      return B.na;
    };
    const getBorderColor = (opt: string) => {
      if (val !== opt) return B.border;
      if (opt === 'Yes') return B.yes;
      if (opt === 'No') return B.no;
      return B.na;
    };

    const showForceInfo =
      (isTruthy(q.force_image_on_yes) && val === 'Yes') ||
      (isTruthy(q.force_image_on_no) && val === 'No');

    return (
      <>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {options.map((opt) => (
            <Box
              key={opt}
              onClick={() => setResponse(q.question_id, opt)}
              sx={{
                border: '1.5px solid',
                borderColor: getBorderColor(opt),
                borderRadius: '8px',
                py: 1.5,
                px: 2.5,
                minWidth: 80,
                cursor: 'pointer',
                bgcolor: getBg(opt),
                color: val === opt ? '#fff' : B.textPrimary,
                fontWeight: 600,
                fontSize: '1rem',
                textAlign: 'center',
                userSelect: 'none',
                transition: 'all 0.15s',
                '&:hover': { opacity: 0.85 },
              }}
            >
              {opt}
            </Box>
          ))}
        </Box>
        {err && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {err}
          </Typography>
        )}

        {showForceInfo && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: B.additionalInfoBg,
              borderRadius: '8px',
              borderLeft: `4px solid ${B.additionalInfoBorder}`,
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block', color: '#92400e' }}>
              Additional details (optional):
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="Describe the condition, issue, or additional context"
              value={additionalNotes[q.question_id] || ''}
              onChange={(e) =>
                setAdditionalNotes((prev) => ({ ...prev, [q.question_id]: e.target.value }))
              }
              sx={{ bgcolor: '#fff', borderRadius: 1 }}
            />
            {!isTruthy(q.image_required) && (
              <PhotoCapture
                questionId={q.question_id}
                captured={imageFiles[q.question_id] || null}
                onCapture={(img) => setImage(q.question_id, img)}
                onRemove={() => removeImage(q.question_id)}
                allowVideo={isTruthy(q.allow_video_attachment)}
              />
            )}
          </Box>
        )}

        {isTruthy(q.image_required) && val !== 'N/A' && (
          <PhotoCapture
            questionId={q.question_id}
            captured={imageFiles[q.question_id] || null}
            onCapture={(img) => setImage(q.question_id, img)}
            onRemove={() => removeImage(q.question_id)}
            required
            error={validationErrors[q.question_id]}
            allowVideo={isTruthy(q.allow_video_attachment)}
          />
        )}
      </>
    );
  };

  // ── Render a single question ───────────────────────────────────────────────

  const renderQuestion = (q: Question) => {
    const val = responses[q.question_id] || '';
    const err = validationErrors[q.question_id];

    // ── Section header ────────────────────────────────────────────────────────
    if (q.question_type === 'text_header') {
      return (
        <Box
          key={q.question_id}
          sx={{
            my: 1,
            py: 2,
            px: 2.5,
            bgcolor: B.sectionHeaderBg,
            borderRadius: '12px',
            borderLeft: `5px solid ${B.sectionHeaderBorder}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{ width: 4, height: 24, bgcolor: B.sectionHeaderBorder, borderRadius: '2px', flexShrink: 0 }}
          />
          <Typography variant="h6" fontWeight={700} sx={{ color: B.textPrimary, letterSpacing: 0.3 }}>
            {q.question_text}
          </Typography>
        </Box>
      );
    }

    return (
      <Card
        key={q.question_id}
        id={`q-${q.question_id}`}
        variant="outlined"
        sx={{
          borderColor: err ? 'error.main' : B.border,
          borderRadius: '12px',
          transition: 'border-color 0.2s',
          bgcolor: B.cardBg,
        }}
      >
        <CardContent sx={{ pb: '16px !important', px: 2.5, pt: 2 }}>
          {/* Question header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <Typography variant="body1" fontWeight={600} sx={{ flex: 1, color: B.textPrimary, lineHeight: 1.5 }}>
              {q.question_text}
            </Typography>
            {q.required && (
              <Chip
                label="Required"
                size="small"
                sx={{
                  bgcolor: '#fef2f2',
                  color: B.no,
                  border: `1px solid ${B.no}`,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 22,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          {/* ── Yes/No ──────────────────────────────────────────────────── */}
          {q.question_type === 'yes_no' && renderYesNoButtons(q, ['Yes', 'No'])}

          {/* ── Yes/No/N/A ──────────────────────────────────────────────── */}
          {q.question_type === 'yes_no_na' && renderYesNoButtons(q, ['Yes', 'No', 'N/A'])}

          {/* ── Multiple choice ──────────────────────────────────────────── */}
          {q.question_type === 'multiple_choice' && (() => {
            const selected = val ? val.split(', ').map((s) => s.trim()).filter(Boolean) : [];
            const max = q.max_selections;
            const min = q.min_selections;
            const atMax = max ? selected.length >= max : false;

            return (
              <Box>
                {q.allow_multiple_selections && (
                  <Typography variant="caption" sx={{ color: B.textSecondary, fontStyle: 'italic', mb: 1, display: 'block' }}>
                    {min && max
                      ? `Select between ${min} and ${max} options`
                      : min
                      ? `Select at least ${min} option${min !== 1 ? 's' : ''}`
                      : max
                      ? `Select up to ${max} option${max !== 1 ? 's' : ''}`
                      : 'Select all that apply'}
                  </Typography>
                )}

                {q.allow_multiple_selections ? (
                  // Checkbox-style list
                  <Box>
                    {(q.options || []).map((opt) => {
                      const isChecked = selected.includes(opt);
                      const disabled = atMax && !isChecked;
                      return (
                        <Box
                          key={opt}
                          onClick={() => !disabled && toggleMultiChoice(q.question_id, opt, max)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1.5,
                            px: 2,
                            mb: 1,
                            bgcolor: isChecked ? '#eff6ff' : '#f8f9fa',
                            borderRadius: '10px',
                            border: '1.5px solid',
                            borderColor: isChecked ? B.primary : '#e0e0e0',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 1,
                            userSelect: 'none',
                            transition: 'all 0.15s',
                          }}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '6px',
                              border: '2px solid',
                              borderColor: B.primary,
                              bgcolor: isChecked ? B.primary : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {isChecked && <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ color: B.textPrimary, fontWeight: isChecked ? 600 : 400, flex: 1 }}
                          >
                            {opt}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  // Pill button list (single select)
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                    {(q.options || []).map((opt) => {
                      const isSelected = val === opt;
                      return (
                        <Box
                          key={opt}
                          onClick={() => setResponse(q.question_id, opt)}
                          sx={{
                            border: '1.5px solid',
                            borderColor: isSelected ? B.primary : B.border,
                            borderRadius: '8px',
                            py: 1.25,
                            px: 2,
                            cursor: 'pointer',
                            bgcolor: isSelected ? B.primary : '#fff',
                            color: isSelected ? '#fff' : B.textPrimary,
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: '0.95rem',
                            userSelect: 'none',
                            transition: 'all 0.15s',
                            '&:hover': { opacity: 0.85 },
                          }}
                        >
                          {opt}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {err && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {err}
                  </Typography>
                )}

                {(isTruthy(q.image_required) || isTruthy(q.allow_video_attachment)) && (
                  <PhotoCapture
                    questionId={q.question_id}
                    captured={imageFiles[q.question_id] || null}
                    onCapture={(img) => setImage(q.question_id, img)}
                    onRemove={() => removeImage(q.question_id)}
                    required={isTruthy(q.image_required)}
                    error={validationErrors[q.question_id + '_img']}
                    allowVideo={isTruthy(q.allow_video_attachment)}
                  />
                )}
              </Box>
            );
          })()}

          {/* ── Text / Number ────────────────────────────────────────────── */}
          {(q.question_type === 'text' || q.question_type === 'number') && (
            <Box>
              <TextField
                fullWidth={q.question_type === 'text'}
                type={q.question_type === 'number' ? 'number' : 'text'}
                multiline={q.question_type === 'text'}
                minRows={q.question_type === 'text' ? 2 : undefined}
                value={val}
                onChange={(e) => setResponse(q.question_id, e.target.value)}
                error={!!err}
                helperText={err}
                placeholder={q.question_type === 'number' ? 'Enter a number' : 'Enter your answer'}
                sx={{ maxWidth: q.question_type === 'number' ? 200 : undefined }}
              />
              {(isTruthy(q.image_required) || isTruthy(q.allow_video_attachment)) && (
                <PhotoCapture
                  questionId={q.question_id}
                  captured={imageFiles[q.question_id] || null}
                  onCapture={(img) => setImage(q.question_id, img)}
                  onRemove={() => removeImage(q.question_id)}
                  required={isTruthy(q.image_required)}
                  error={validationErrors[q.question_id]}
                  allowVideo={isTruthy(q.allow_video_attachment)}
                />
              )}
            </Box>
          )}

          {/* ── Date ────────────────────────────────────────────────────── */}
          {q.question_type === 'date' && (
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                <CalendarTodayIcon
                  sx={{ position: 'absolute', left: 12, color: B.textSecondary, fontSize: 20, pointerEvents: 'none', zIndex: 1 }}
                />
                <TextField
                  type="date"
                  value={val}
                  onChange={(e) => setResponse(q.question_id, e.target.value)}
                  error={!!err}
                  helperText={err}
                  InputLabelProps={{ shrink: true }}
                  sx={{ maxWidth: 220, '& .MuiInputBase-input': { pl: 5 } }}
                />
              </Box>
              {(isTruthy(q.image_required) || isTruthy(q.allow_video_attachment)) && (
                <PhotoCapture
                  questionId={q.question_id}
                  captured={imageFiles[q.question_id] || null}
                  onCapture={(img) => setImage(q.question_id, img)}
                  onRemove={() => removeImage(q.question_id)}
                  required={isTruthy(q.image_required)}
                  error={validationErrors[q.question_id]}
                  allowVideo={isTruthy(q.allow_video_attachment)}
                />
              )}
            </Box>
          )}

          {/* ── Email check ──────────────────────────────────────────────── */}
          {q.question_type === 'email_check' && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  p: 1.5,
                  bgcolor: '#eff6ff',
                  borderRadius: '8px',
                  border: `1px solid #bfdbfe`,
                }}
              >
                <EmailIcon sx={{ color: B.primary, fontSize: 20 }} />
                <Typography variant="caption" fontWeight={500} color={B.primary}>
                  A PDF copy of this check will be emailed to this address on completion
                </Typography>
              </Box>
              <TextField
                type="email"
                fullWidth
                value={val}
                onChange={(e) => setResponse(q.question_id, e.target.value)}
                error={!!err}
                helperText={err}
                placeholder="customer@example.com"
                sx={{ maxWidth: 360 }}
              />
            </Box>
          )}

          {/* ── Signature ────────────────────────────────────────────────── */}
          {q.question_type === 'signature' && (
            <Box>
              <SignaturePad
                value={val}
                onChange={(v) => setResponse(q.question_id, v)}
                borderColor={B.border}
                clearButtonColor={B.no}
              />
              {err && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {err}
                </Typography>
              )}
            </Box>
          )}

          {/* ── Image / video type ───────────────────────────────────────── */}
          {q.question_type === 'image_type' && (
            <PhotoCapture
              questionId={q.question_id}
              captured={imageFiles[q.question_id] || null}
              onCapture={(img) => setImage(q.question_id, img)}
              onRemove={() => removeImage(q.question_id)}
              required={q.required}
              error={err}
              allowVideo={isTruthy(q.allow_video_attachment)}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  // ── Page shell ─────────────────────────────────────────────────────────────

  const renderHeader = () => (
    <Box
      sx={{
        bgcolor: B.cardBg,
        borderBottom: `1px solid ${B.border}`,
        py: 2.5,
        px: 2,
        textAlign: 'center',
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800;900&display=swap"
        rel="stylesheet"
      />
      <img
        src={`${process.env.PUBLIC_URL}/safety.png`}
        alt="Check-Now"
        style={{ width: 56, height: 56, objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}
      />
      <Box sx={{ mt: 0.75 }}>
        <BrandWordmark variant="h5" />
      </Box>
      {tokenData && (
        <Typography variant="body2" sx={{ color: B.textSecondary, mt: 0.25 }}>
          {tokenData.businessName}
        </Typography>
      )}
    </Box>
  );

  // ── States ─────────────────────────────────────────────────────────────────

  if (pageState === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: B.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: B.primary }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Loading check…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (pageState === 'error') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg }}>
        {renderHeader()}
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: '16px', border: `1.5px solid ${B.border}` }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: B.no, mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Unable to load check
              </Typography>
              <Typography color="text.secondary">{errorMessage}</Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (pageState === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg }}>
        {renderHeader()}
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card variant="outlined" sx={{ borderRadius: '16px', border: `1.5px solid ${B.border}` }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <CheckCircleIcon sx={{ fontSize: 72, color: B.yes, mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Check Completed!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Your responses have been submitted successfully. Thank you,{' '}
                <strong>{name}</strong>.
              </Typography>
              <Chip
                label={tokenData?.templateName}
                sx={{
                  bgcolor: '#eff6ff',
                  color: B.primary,
                  border: `1px solid ${B.primary}`,
                  fontWeight: 600,
                  px: 1,
                }}
              />
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // ── Step 1: Enter name + email ─────────────────────────────────────────────

  if (pageState === 'details') {
    const questionCount = tokenData?.questions.filter(
      (q) => q.question_type !== 'text_header'
    ).length ?? 0;

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: B.bg }}>
        {renderHeader()}
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card
            variant="outlined"
            sx={{ borderRadius: '16px', border: `1.5px solid ${B.border}` }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: B.textPrimary }}>
                {tokenData?.templateName}
              </Typography>

              {tokenData?.customMessage && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 2,
                    borderRadius: '10px',
                    bgcolor: '#eff6ff',
                    color: '#1e40af',
                    border: `1px solid #bfdbfe`,
                    '& .MuiAlert-icon': { color: B.primary },
                  }}
                >
                  {tokenData.customMessage}
                </Alert>
              )}

              <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                Please confirm your details before starting the check.
              </Typography>

              {detailsError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: '10px' }}
                >
                  {detailsError}
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleDetailsNext()}
                />
                <TextField
                  fullWidth
                  required
                  label="Your Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleDetailsNext()}
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDetailsNext}
                  fullWidth
                  sx={{
                    py: 1.75,
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: '10px',
                    bgcolor: B.primary,
                    '&:hover': { bgcolor: '#1d4ed8' },
                  }}
                >
                  Start Check — {questionCount} question{questionCount !== 1 ? 's' : ''}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // ── Step 2: Questions ──────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: B.bg }}>
      {renderHeader()}

      {/* Sticky progress bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#fff',
          borderBottom: `1px solid ${B.border}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.75,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: B.textPrimary }} noWrap>
            {tokenData?.templateName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>
            {answeredCount} / {totalAnswerable}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Box
            sx={{
              height: 8,
              bgcolor: '#e5e5ea',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progress}%`,
                bgcolor: B.progressFill,
                borderRadius: '4px',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Stack spacing={2}>
          {visibleQuestions.map(renderQuestion)}

          {Object.keys(validationErrors).length > 0 && (
            <Alert
              severity="error"
              sx={{ borderRadius: '10px' }}
            >
              Please answer all required questions before submitting.
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={validateAndSubmit}
            disabled={pageState === 'submitting'}
            fullWidth
            sx={{
              py: 1.75,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: '10px',
              bgcolor: B.primary,
              '&:hover': { bgcolor: '#1d4ed8' },
              '&.Mui-disabled': { bgcolor: '#93c5fd', color: '#fff' },
            }}
            startIcon={
              pageState === 'submitting' ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {pageState === 'submitting' ? 'Submitting…' : 'Submit Check'}
          </Button>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            Completing as <strong>{name}</strong> ({email})
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default PublicCompleteCheck;
